import { useCallback, useRef, useState } from "react";

import { useJitsu } from "@jitsu/react";
import { toast } from "react-hot-toast";

import { MultipartUploadError, UploadedMedia } from "@/client/media-upload";
import {
  CapturedMedia,
  RecordingDiagnostics,
} from "@/components/webcam-capture/use-webcam-capture";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppDispatch, useAppSelector } from "@/data/hooks";
import {
  captureRecordingError,
  captureRecordingWarning,
  recordingBreadcrumb,
} from "@/features/socratic/recording-diagnostics";
import {
  selectIsUploading,
  setIsUploading,
} from "@/features/socratic/socratic-slice";
import {
  CompleteSocraticTurnError,
  useCompleteSocraticTurnMutation,
} from "@/generated/graphql";
import { useMultipartUpload } from "@/hooks/use-multipart-upload";

interface Props {
  workId: string;
  turnId: string | undefined;
  dialogueId: string;
  onCapture?: () => void;
}

/**
 * Hook that handles uploading a recorded Socratic video response and
 * completing the turn via the GraphQL mutation.
 *
 * The upload is resumable (see {@link useMultipartUpload}): a network drop does
 * not discard the recording. On failure the hook returns a non-null
 * `retryUpload`, which the UI surfaces as a "Retry upload" button — the student
 * reconnects (the `ConnectionBar` tells them when), then retries the same
 * recording without re-recording. A retry resumes the multipart upload
 * (re-signing only the parts that did not land); if the upload already finished
 * and only the mutation failed, it skips straight to the mutation.
 */
export function useCompleteSocraticTurn(
  props: Props
): [
  completeTurn: (
    media: CapturedMedia,
    durationSeconds: number,
    diagnostics?: RecordingDiagnostics
  ) => Promise<void>,
  isUploading: boolean,
  retryUpload: (() => void) | null,
] {
  const { workId, turnId, dialogueId, onCapture } = props;
  const { track } = useJitsu();
  const dispatch = useAppDispatch();
  const isUploading = useAppSelector(selectIsUploading);
  const [completeTurnMutation] = useCompleteSocraticTurnMutation();
  const [uploadFailed, setUploadFailed] = useState(false);

  const upload = useMultipartUpload();

  const lastCaptureRef = useRef<{
    media: CapturedMedia;
    durationSeconds: number;
    diagnostics?: RecordingDiagnostics;
  } | null>(null);
  // Media from a finished upload, kept so a retry after a mutation-only failure
  // does not re-upload the file.
  const uploadedRef = useRef<UploadedMedia | null>(null);

  const runTurn = useCallback(
    async (
      media: CapturedMedia,
      durationSeconds: number,
      diagnostics: RecordingDiagnostics | undefined,
      { isRetry }: { isRetry: boolean }
    ) => {
      if (turnId == null) return;
      recordingBreadcrumb("upload attempt", {
        dialogue_id: dialogueId,
        is_retry: isRetry,
        online: navigator.onLine,
      });
      dispatch(setIsUploading(true));
      setUploadFailed(false);
      try {
        const fileData =
          uploadedRef.current ??
          (isRetry ? await upload.resume() : await upload.start(media.file));
        uploadedRef.current = fileData;

        const result = await completeTurnMutation({
          variables: {
            workId,
            input: {
              turnId,
              mediaId: fileData.id,
              mediaUrl: fileData.url,
              durationSeconds,
            },
          },
        });

        if (result.data?.completeSocraticTurn?.dialogue) {
          uploadedRef.current = null;
          lastCaptureRef.current = null;
          // The signal that was previously invisible: a recording that uploaded
          // successfully but is missing the audio or video we expected.
          if (
            diagnostics != null &&
            (diagnostics.audioOutcome !== "present" ||
              diagnostics.videoOutcome === "lost_mid_recording" ||
              diagnostics.videoOutcome === "missing_at_start")
          ) {
            captureRecordingWarning(
              "Socratic recording submitted degraded",
              {
                audio_outcome: diagnostics.audioOutcome,
                video_outcome: diagnostics.videoOutcome,
              },
              {
                dialogue_id: dialogueId,
                duration_seconds: durationSeconds,
                track_summary: diagnostics.trackSummary,
              }
            );
          }
          toast.success("Your recording has been completed");
        } else if (result.data?.completeSocraticTurn.error != null) {
          const errorMessage =
            result.data.completeSocraticTurn.error ===
            CompleteSocraticTurnError.AlreadyCompleted
              ? "This question has already been answered."
              : "Couldn't save your response.";

          toast.error(
            `${errorMessage} Please return to the main page and try again.`
          );
        }
      } catch (e) {
        // A non-null uploadedRef means the upload landed and only the
        // mutation failed — retry skips straight to the mutation.
        const uploadSucceeded = uploadedRef.current != null;
        const failurePhase = uploadSucceeded
          ? "complete_turn_failed"
          : "upload_failed";
        track(JitsuEvent.VIDEO_RESPONSE_FAILED, {
          error: failurePhase,
          error_message: e instanceof Error ? e.message : String(e),
          dialogue_id: dialogueId,
        });

        if (e instanceof MultipartUploadError) {
          captureRecordingError(
            e,
            // tag to help us find the student and their recording
            { work_id: workId },
            {
              dialogue_id: dialogueId,
              turn_id: turnId,
              online: navigator.onLine,
              file_size: media.file.size,
              duration_seconds: durationSeconds,
              mime: media.file.type,
              media_id: e.mediaId,
              upload_id: e.uploadId,
              status: e.status,
            }
          );
        }
        toast.error(
          uploadSucceeded
            ? "Sorry, we could not save your response. Check your internet connection and try again"
            : "Sorry, video upload failed. Check your internet connection and try again."
        );
        setUploadFailed(true);
      } finally {
        dispatch(setIsUploading(false));
      }
    },
    [turnId, workId, dialogueId, completeTurnMutation, track, upload, dispatch]
  );

  const onCaptureReady = useCallback(
    async (
      media: CapturedMedia,
      durationSeconds: number,
      diagnostics?: RecordingDiagnostics
    ) => {
      lastCaptureRef.current = { media, durationSeconds, diagnostics };
      uploadedRef.current = null;
      onCapture?.();
      await runTurn(media, durationSeconds, diagnostics, { isRetry: false });
    },
    [onCapture, runTurn]
  );

  const retryUpload = useCallback(() => {
    const last = lastCaptureRef.current;
    if (last == null) return;
    runTurn(last.media, last.durationSeconds, last.diagnostics, {
      isRetry: true,
    });
  }, [runTurn]);

  return [onCaptureReady, isUploading, uploadFailed ? retryUpload : null];
}
